/*
Esc on a MessageBox does what its X does (XOL-629).

With the default `distinguishCancelAndClose: false` that is the Cancel button's own
`handleAction('cancel')`, so a `$confirm` / `$msgbox` rejects with 'cancel' on Esc just
as it does on the Cancel button. Element UI 2.15's own Esc handler (PopupManager, a
window BUBBLE listener registered when the module first evaluates) always calls
`handleClose()` -> `handleAction('close')`, whatever `distinguishCancelAndClose` says,
and there is no per-call option that changes it.

Why this acts in the CAPTURE phase and stops propagation: Element's bubble listener
would otherwise run next and call `handleClose()` on whatever is NOW the top popup, so
it would close the el-dialog / el-drawer under the box. If the box were still closing,
it would also overwrite `this.action` with 'close' before the deferred callback reads
it. Two things keep XOL-609 rule 1 (Esc over an Element modal never also closes the
drawer behind it): stopping the event at window-capture means LayerHost's bubble
listener never sees the key, and `preventDefault()` would make it ignore the key anyway
(LayerHost's rule 5, `defaultPrevented`). Keep both: only `stopPropagation` protects an
el-dialog / el-drawer under the box (escCancel.spec.js case 5). `stopPropagation` (not `stopImmediatePropagation`) lets
LayerHost's own window-capture recorder still run; it does nothing harmful.

Why el-dialog / el-drawer are NOT handled here: acting in the capture phase would beat
target-level Esc owners such as an `el-select` dropdown inside a dialog, and acting in
the bubble phase loses the race to Element's listener. Those components bind
`:before-close` to their Cancel handler instead (see conventions.md, "Esc = Cancel on
modals").

Trade-off: while a MessageBox is on top, controls inside it never see Esc, and the
toasts / notifications that close on a document keydown survive the key.

Vue 3: Element Plus's MessageBox probably sends Esc through the same
`distinguishCancelAndClose ? 'close' : 'cancel'` path as the X. Verify, then delete this
file and its install in main.js (vue3-migration-status.md).

ES5, no optional chaining or nullish coalescing.
------------------------------------------------------------------------ */

import { PopupManager } from 'element-ui/lib/utils/popup'

var installed = null

export function onEscCapture (event) {
  // Element's own check, exactly: no `repeat` / `isComposing` conditions it does not have.
  if (!event || event.keyCode !== 27) {
    return
  }

  var stack = PopupManager.modalStack

  if (!stack || stack.length === 0) {
    return
  }

  var top = stack[stack.length - 1]
  var vm = top && PopupManager.getInstance(top.id)

  // Only a MessageBox: an el-dialog / el-drawer has no handleAction.
  if (!vm || typeof vm.handleAction !== 'function' || !vm.visible || !vm.closeOnPressEscape) {
    return
  }

  event.preventDefault()
  event.stopPropagation()

  vm.handleAction(vm.distinguishCancelAndClose ? 'close' : 'cancel')
}

export function installEscCancel (win) {
  var target = win || window

  if (installed) {
    return installed
  }

  target.addEventListener('keydown', onEscCapture, true)

  installed = function uninstall () {
    target.removeEventListener('keydown', onEscCapture, true)
    installed = null
  }

  return installed
}
